import { useState, type ReactNode } from "react";
import {
  Navigate,
  NavLink,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Clock3,
  AlertTriangle,
  BarChart3,
  Bell,
  Building2,
  CalendarDays,
  CalendarRange,
  ChevronDown,
  ClipboardList,
  Clock,
  Cloud,
  CreditCard,
  FileCheck2,
  FileText,
  Gauge,
  History,
  Home,
  KeyRound,
  LayoutGrid,
  ListChecks,
  LogOut,
  Map,
  Menu,
  MonitorSmartphone,
  Plane,
  Plug,
  Scale,
  Settings,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  User,
  Users,
  Workflow,
  X,
} from "lucide-react";
import type { Permission } from "@wulftime/core";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Avatar, cx } from "./ui";
import { CompanyLogo } from "./company-logo";
import { PaymentGate } from "./payment-gate";
import { InstallBanner } from './device-app';
import { Logo } from "./logo";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  perm?: Permission | Permission[];
  feature?: string;
}

const ADMIN_NAV: { section: string; items: NavItem[] }[] = [
  {
    section: "Operación",
    items: [
      {
        to: "/admin",
        label: "Panel",
        icon: <Gauge className="h-4 w-4" />,
        perm: "time_records.view",
      },
      {
        to: "/admin/records",
        label: "Registro horario",
        icon: <Clock className="h-4 w-4" />,
        perm: "time_records.view",
      },
      {
        to: "/admin/anomalies",
        label: "Anomalías",
        icon: <AlertTriangle className="h-4 w-4" />,
        perm: "time_records.view",
      },
      {
        to: "/admin/incidents",
        label: "Incidencias",
        icon: <ClipboardList className="h-4 w-4" />,
        perm: "incidents.view",
      },
      {
        to: "/admin/corrections",
        label: "Correcciones",
        icon: <FileCheck2 className="h-4 w-4" />,
        perm: "corrections.view",
      },
      {
        to: "/admin/late-exits",
        label: "Salidas tardías",
        icon: <Clock3 className="h-4 w-4" />,
        perm: "time_records.view",
      },
      {
        to: "/admin/absences",
        label: "Vacaciones y ausencias",
        icon: <Plane className="h-4 w-4" />,
        perm: "absences.view",
      },
      {
        to: "/admin/gps",
        label: "GPS",
        icon: <Map className="h-4 w-4" />,
        perm: "gps.view",
        feature: "gps",
      },
    ],
  },
  {
    section: "Organización",
    items: [
      {
        to: "/admin/employees",
        label: "Trabajadores",
        icon: <Users className="h-4 w-4" />,
        perm: "employees.view",
      },
      {
        to: "/admin/centers",
        label: "Centros",
        icon: <Building2 className="h-4 w-4" />,
        perm: ["centers.manage", "company.view"],
      },
      {
        to: "/admin/org",
        label: "Departamentos y equipos",
        icon: <Workflow className="h-4 w-4" />,
        perm: "org.manage",
      },
      {
        to: "/admin/users",
        label: "Usuarios y roles",
        icon: <Shield className="h-4 w-4" />,
        perm: "users.manage",
      },
    ],
  },
  {
    section: "Planificación",
    items: [
      {
        to: "/admin/schedules",
        label: "Horarios",
        icon: <CalendarRange className="h-4 w-4" />,
        perm: "schedules.manage",
      },
      {
        to: "/admin/shifts",
        label: "Turnos y cuadrante",
        icon: <LayoutGrid className="h-4 w-4" />,
        perm: ["schedules.manage", "time_records.view"],
      },
      {
        to: "/admin/calendars",
        label: "Calendario laboral",
        icon: <CalendarDays className="h-4 w-4" />,
        perm: "calendars.manage",
      },
      {
        to: "/admin/policies",
        label: "Políticas",
        icon: <SlidersHorizontal className="h-4 w-4" />,
        perm: "policies.manage",
      },
    ],
  },
  {
    section: "Informes y control",
    items: [
      {
        to: "/admin/reports",
        label: "Informes",
        icon: <BarChart3 className="h-4 w-4" />,
        perm: "reports.view",
      },
      {
        to: "/admin/audit",
        label: "Auditoría",
        icon: <ListChecks className="h-4 w-4" />,
        perm: "audit.view",
      },
      {
        to: "/admin/cloud",
        label: "Cloud",
        icon: <Cloud className="h-4 w-4" />,
        perm: "custody.manage",
        feature: "cloud",
      },
      {
        to: "/admin/custody",
        label: "Custody",
        icon: <ShieldCheck className="h-4 w-4" />,
        perm: "custody.manage",
      },
    ],
  },
  {
    section: "Configuración",
    items: [
      {
        to: "/admin/kiosks",
        label: "Kioscos",
        icon: <MonitorSmartphone className="h-4 w-4" />,
        perm: "kiosks.manage",
        feature: "kiosk",
      },
      {
        to: "/admin/notifications",
        label: "Notificaciones",
        icon: <Bell className="h-4 w-4" />,
        perm: "notifications.manage",
      },
      {
        to: "/admin/integrations",
        label: "Integraciones",
        icon: <Plug className="h-4 w-4" />,
        perm: "integrations.manage",
      },
      {
        to: "/admin/legal",
        label: "Legal y privacidad",
        icon: <Scale className="h-4 w-4" />,
        perm: ["legal.manage", "audit.view"],
      },
      {
        to: "/admin/subscription",
        label: "Suscripción",
        icon: <CreditCard className="h-4 w-4" />,
        perm: "billing.manage",
      },
      {
        to: "/admin/settings",
        label: "Empresa y seguridad",
        icon: <Settings className="h-4 w-4" />,
        perm: "company.manage",
      },
    ],
  },
];

function NotificationBell({ to }: { to: string }) {
  const { data } = useQuery({
    queryKey: ["notifications", "unread"],
    queryFn: () =>
      api.get<{ unread: number }>(
        "/api/v1/me/notifications?unread=true&limit=1",
      ),
    refetchInterval: 60_000,
  });
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="relative rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
      aria-label={`Notificaciones${data?.unread ? ` (${data.unread} sin leer)` : ""}`}
    >
      <Bell className="h-5 w-5" />
      {data?.unread ? (
        <span className="absolute right-1 top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold text-white">
          {data.unread > 9 ? "9+" : data.unread}
        </span>
      ) : null}
    </button>
  );
}

function UserMenu({ area }: { area: "admin" | "worker" | "platform" }) {
  const { me, logout, switchCompany, isAdminArea } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg p-1 pr-2 hover:bg-slate-100"
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Avatar name={me.user.fullName} size="sm" />
        <span className="hidden max-w-[160px] truncate text-sm font-medium text-slate-700 md:inline">
          {me.user.fullName}
        </span>
        <ChevronDown className="h-4 w-4 text-slate-400" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-1.5 shadow-lg"
          onMouseLeave={() => setOpen(false)}
        >
          <div className="px-3 py-2">
            <p className="truncate text-sm font-medium">{me.user.fullName}</p>
            <p className="truncate text-xs text-slate-500">{me.user.email}</p>
          </div>
          {me.companies.length > 1 && (
            <div className="border-t border-slate-100 py-1">
              <p className="px-3 py-1 text-xs font-semibold uppercase text-slate-400">
                Empresa
              </p>
              {me.companies.map((c) => (
                <button
                  key={c.id}
                  role="menuitem"
                  onClick={() => switchCompany(c.id).then(() => navigate("/"))}
                  className={cx(
                    "block w-full rounded-lg px-3 py-1.5 text-left text-sm hover:bg-slate-50",
                    c.id === me.company?.id && "font-semibold text-brand-800",
                  )}
                >
                  {c.name}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-slate-100 py-1">
            {area !== "worker" && me.company?.employee && (
              <button
                role="menuitem"
                onClick={() => navigate("/app")}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50"
              >
                <Clock className="h-4 w-4" /> Mi fichaje
              </button>
            )}
            {area !== "admin" && isAdminArea && (
              <button
                role="menuitem"
                onClick={() => navigate("/admin")}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50"
              >
                <Gauge className="h-4 w-4" /> Panel de empresa
              </button>
            )}
            {area !== "platform" && me.user.isSuperadmin && (
              <button
                role="menuitem"
                onClick={() => navigate("/platform")}
                className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50"
              >
                <Shield className="h-4 w-4" /> Plataforma WulfTime
              </button>
            )}
            <button
              role="menuitem"
              onClick={() =>
                navigate(area === "admin" ? "/admin/profile" : "/app/profile")
              }
              className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              <User className="h-4 w-4" /> Perfil y seguridad
            </button>
            <button
              role="menuitem"
              onClick={() => logout().then(() => navigate("/login"))}
              className="flex w-full items-center gap-2 rounded-lg px-3 py-1.5 text-sm text-red-700 hover:bg-red-50"
            >
              <LogOut className="h-4 w-4" /> Cerrar sesión
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Condiciones del servicio y contrato de encargo pendientes de aceptar (alta anterior o versión nueva). */
function TermsBanner() {
  const { me, reload } = useAuth();
  const [busy, setBusy] = useState(false);
  const pending = (me?.company as { termsPending?: { slug: string; title: string }[] } | null)?.termsPending ?? [];
  if (!pending.length || me?.supportAccess) return null;
  const accept = async () => {
    setBusy(true);
    try {
      await api.post("/api/v1/legal/terms/accept", { documents: pending.map((d) => d.slug) });
      await reload();
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-brand-900 px-4 py-2 text-center text-xs text-white">
      <span>
        Para seguir usando WulfTime, revisa y acepta{" "}
        {pending.map((d, i) => (
          <span key={d.slug}>
            {i > 0 && (i === pending.length - 1 ? " y " : ", ")}
            <a href={`/legal/${d.slug}`} target="_blank" rel="noreferrer" className="font-semibold underline">
              {d.title.toLowerCase()}
            </a>
          </span>
        ))}
        .
      </span>
      <button type="button" onClick={accept} disabled={busy} className="rounded-md bg-white px-2.5 py-1 font-semibold text-brand-900 hover:bg-brand-50 disabled:opacity-60">
        {busy ? "Guardando…" : "He leído y acepto"}
      </button>
    </div>
  );
}

function SupportBanner() {
  const { me, can } = useAuth();
  if (
    me?.company?.plan.status === "PAST_DUE" &&
    !me.supportAccess &&
    can("billing.manage")
  )
    return (
      <a
        href="/admin/subscription"
        className="block bg-red-600 px-4 py-1.5 text-center text-xs font-semibold text-white"
      >
        Hay facturas vencidas: los fichajes siguen funcionando, pero los módulos
        premium están en pausa. Regulariza el pago en Suscripción.
      </a>
    );
  if (!me?.supportAccess) return null;
  return (
    <div className="bg-amber-500 px-4 py-1.5 text-center text-xs font-semibold text-amber-950">
      Acceso de soporte de solo lectura · todas las consultas quedan registradas
      en la auditoría de la empresa
    </div>
  );
}

export function AdminLayout() {
  const { me, can, hasFeature, isAdminArea } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const loc = useLocation();
  if (!isAdminArea && !me?.supportAccess)
    return <Navigate to={me?.company?.employee ? "/app" : "/"} replace />;
  const nav = ADMIN_NAV.map((s) => ({
    ...s,
    items: s.items.filter(
      (i) => (!i.perm || can(i.perm)) && (!i.feature || hasFeature(i.feature)),
    ),
  })).filter((s) => s.items.length);
  const sidebar = (
    <nav aria-label="Navegación principal" className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-2 border-b border-white/10 px-5">
        <Logo dark />
      </div>
      <div className="px-3 py-3">
        <div className="flex items-center gap-2.5 rounded-lg bg-white/5 px-3 py-2">
          <CompanyLogo tile className="h-8 max-w-[72px]" />
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-white">
              {me?.company?.name}
            </p>
            <p className="text-xs text-slate-400">
              {me?.company?.plan.name ?? "Sin plan"}
            </p>
          </div>
        </div>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto px-3 pb-6">
        {nav.map((s) => (
          <div key={s.section}>
            <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              {s.section}
            </p>
            {s.items.map((i) => (
              <NavLink
                key={i.to}
                to={i.to}
                end={i.to === "/admin"}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) =>
                  cx(
                    "flex items-center gap-2.5 rounded-lg px-3 py-1.5 text-sm transition",
                    isActive
                      ? "bg-brand-700 text-white"
                      : "text-slate-300 hover:bg-slate-800 hover:text-white",
                  )
                }
              >
                {i.icon}
                {i.label}
              </NavLink>
            ))}
          </div>
        ))}
      </div>
    </nav>
  );
  return (
    <div className="min-h-screen">
      <a href="#main" className="skip-link">
        Saltar al contenido
      </a>
      <SupportBanner />
      <TermsBanner />
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 bg-brand-950 lg:block">
        {sidebar}
      </aside>
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div
            className="absolute inset-0 bg-slate-900/50"
            onClick={() => setMobileOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 bg-brand-950">
            <button
              className="absolute right-3 top-4 rounded-lg p-1 text-slate-400"
              onClick={() => setMobileOpen(false)}
              aria-label="Cerrar menú"
            >
              <X className="h-5 w-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-4 backdrop-blur lg:px-8">
          <button
            className="rounded-lg p-2 text-slate-600 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Abrir menú"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden text-sm text-slate-500 lg:block">
            {loc.pathname === "/admin" ? "Resumen de hoy" : ""}
          </div>
          <div className="flex items-center gap-1">
            <NotificationBell to="/admin/my-notifications" />
            <UserMenu area="admin" />
          </div>
        </header>
        <main id="main" className="mx-auto max-w-[1400px] px-4 py-6 lg:px-8">
          <InstallBanner />
          {me?.company?.billing?.paymentMethodMissing &&
          can("billing.manage") &&
          !me.supportAccess ? (
            <PaymentGate />
          ) : (
            <Outlet />
          )}
        </main>
      </div>
    </div>
  );
}

const WORKER_NAV = [
  { to: "/app", label: "Fichar", icon: Home, end: true },
  { to: "/app/history", label: "Historial", icon: History },
  { to: "/app/requests", label: "Solicitudes", icon: ClipboardList },
  { to: "/app/absences", label: "Ausencias", icon: Plane },
  { to: "/app/profile", label: "Perfil", icon: User },
];

function WorkerBrand() {
  const { me } = useAuth();
  if (!me?.company?.logoVersion) return <Logo />;
  return (
    <span className="flex min-w-0 items-center gap-2.5">
      <CompanyLogo className="h-9 max-w-[150px]" />
      <span className="hidden truncate text-sm font-semibold text-slate-800 sm:block">
        {me.company.name}
      </span>
    </span>
  );
}

export function WorkerLayout() {
  return (
    <div className="min-h-screen pb-20 md:pb-0">
      <a href="#main" className="skip-link">
        Saltar al contenido
      </a>
      <SupportBanner />
      <header className="sticky top-0 z-20 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-3xl items-center justify-between px-4">
          <WorkerBrand />
          <nav
            className="hidden items-center gap-1 md:flex"
            aria-label="Navegación"
          >
            {WORKER_NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  cx(
                    "rounded-lg px-3 py-1.5 text-sm font-medium",
                    isActive
                      ? "bg-brand-50 text-brand-800"
                      : "text-slate-600 hover:bg-slate-100",
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-1">
            <NotificationBell to="/app/notifications" />
            <UserMenu area="worker" />
          </div>
        </div>
      </header>
      <main id="main" className="mx-auto max-w-3xl px-4 py-5">
        <InstallBanner />
        <Outlet />
      </main>
      <nav
        className="fixed inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white pb-[env(safe-area-inset-bottom)] md:hidden"
        aria-label="Navegación inferior"
      >
        <div className="grid grid-cols-5">
          {WORKER_NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                cx(
                  "flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium",
                  isActive ? "text-brand-700" : "text-slate-500",
                )
              }
            >
              <n.icon className="h-5 w-5" aria-hidden />
              {n.label}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}

const PLATFORM_NAV: [string, string][] = [
  ["/platform", "Resumen"],
  ["/platform/companies", "Empresas"],
  ["/platform/plans", "Planes"],
  ["/platform/billing", "Facturación"],
  ["/platform/audit", "Auditoría"],
];

function PlatformNavLinks() {
  return (
    <>
      {PLATFORM_NAV.map(([to, l]) => (
        <NavLink
          key={to}
          to={to}
          end={to === "/platform"}
          className={({ isActive }) =>
            cx(
              "shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-sm",
              isActive
                ? "bg-slate-700 text-white"
                : "text-slate-300 hover:text-white",
            )
          }
        >
          {l}
        </NavLink>
      ))}
    </>
  );
}

export function PlatformLayout() {
  const { me } = useAuth();
  if (!me?.user.isSuperadmin) return <Navigate to="/" replace />;
  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-20 border-b border-slate-800 bg-slate-900">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4">
          <div className="flex items-center gap-6">
            <Logo dark />
            <span className="rounded bg-amber-400 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-950">
              Plataforma
            </span>
            <nav className="hidden gap-1 md:flex" aria-label="Navegación">
              <PlatformNavLinks />
            </nav>
          </div>
          <div className="rounded-lg bg-white">
            <UserMenu area="platform" />
          </div>
        </div>
        <nav
          className="flex gap-1 overflow-x-auto border-t border-slate-800 px-3 py-2 md:hidden"
          aria-label="Navegación"
        >
          <PlatformNavLinks />
        </nav>
      </header>
      <main className="mx-auto max-w-7xl px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

export { FileText, KeyRound };
